/**
 * IMPORTANT: Import Order Requirements
 *
 * When importing ContractViewerModal or other components that depend on contractDetailApi,
 * ensure they are imported AFTER the contractDetailApi import to avoid circular dependencies.
 *
 * The correct order is:
 * 1. All regular imports
 * 2. contractDetailApi from "@/src/store/apis/contracts/contractDetail"
 * 3. Components that depend on contractDetailApi (like ContractViewerModal)
 */

import AddSvg from "@/src/assets/icons/icon-add-blue.svg";
import ClockIconSvg from "@/src/assets/icons/icon-clock-gray5.svg";
import { ReactComponent as EditSvg } from "@/src/assets/icons/icon-edit.svg";
import PreviewSvg from "@/src/assets/icons/contract/icon-preview.svg";
import { Button } from "@/src/components/atom/Button";
import Icon from "@/src/components/atom/Icon";
import Tooltip from "@/src/components/atom/Tooltip";
import Typo from "@/src/components/atom/Typo";
import SectionCardWithoutHeader from "@/src/components/molecule/SectionCardWithoutHeader";
import IMPORTER_PRIVATE_PATH from "@/src/routes/importer/path";
import colorSet from "@/src/styles/color";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { keyframes, styled } from "styled-components";
import BuyerCard from "./card/BuyerCard";
import ContractInformationCard from "./card/ContractInformationCard";
import { getContractCurrentVersion } from "@/src/utils/getContractCurrentVersion";
import { isNull, isUndefined } from "@/src/utils/is";
import ContractProgressBar from "@/src/components/organism/ContractProgressBar";
import { lazy, Suspense, useState } from "react";
import Loader from "@/src/components/atom/Loader";
import dayjs from "dayjs";
import useAlert from "@/src/hooks/useAlert";
import {
  useGetExportersQuery,
  useSendCompanyLinkCodeMutation,
} from "@/src/store/apis/corporate/company";
import useContentLoading from "@/src/hooks/useContentLoading";
import { useContractProgress } from "@/src/hooks/useContractProgress";
import NoRegisteredSignatureAlertDialog from "@/src/components/organism/NoRegisteredSignatureAlertDialog";
import SignatureRequestAlertDialog from "@/src/components/organism/SignatureRequestAlertDialog";
import SignatureSelectDialog from "@/src/components/organism/SignatureSelectDialog";
import AlertDialog from "@/src/components/atom/AlertDialog";
import ClientIntegrationInviteCodeDialog from "@/src/components/organism/ClientIntegrationInviteCodeDialog";
import { useGetBuyerEmployeesQuery } from "@/src/store/apis/client/buyer";
import { GenerateBuyerEmployeeDto } from "@/src/store/apis/client/buyer/interface";

// API imports must come before components that depend on them
import {
  contractDetailApi,
  useConfirmImporterPoMutation,
  useConfirmImporterScMutation,
  useLazyGetImporterPoHistoriesQuery,
  useLazyGetImporterScHistoriesQuery,
} from "@/src/store/apis/contracts/contractDetail";

// Lazy load the modal to prevent circular dependency
const ContractViewerModal = lazy(
  () => import("@/src/components/organism/ContractViewerModal"),
);

interface ContractInformationTabProps {
  isEditAuth: boolean;
  balanceOfContractV2: string;
  percentOfShipment: number;
  totalShippingWeightV2: string;
  historyDialogOn: () => void;
  isOnlyReceivedSc?: boolean;
  isDisabledEdit: boolean;
  onContractNoticeAction?: (actionType?: string) => void;
  rejectReason?: string;
  poNo?: string;
  scNo?: string;
  onOpenSignatureViewer?: (type: "PO" | "SC") => void;
}

const ContractInformationTab = ({
  isEditAuth,
  balanceOfContractV2,
  percentOfShipment,
  totalShippingWeightV2,
  historyDialogOn,
  isOnlyReceivedSc,
  isDisabledEdit,
  onContractNoticeAction,
  rejectReason,
  poNo,
  scNo,
  onOpenSignatureViewer,
}: ContractInformationTabProps) => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const alert = useAlert();
  const { handleContentLoadingOn, handleContentLoadingOff } =
    useContentLoading();
  const [isContractViewerOpen, setIsContractViewerOpen] = useState(false);
  const [showNoSignatureDialog, setShowNoSignatureDialog] = useState(false);
  const [showSignatureRequestDialog, setShowSignatureRequestDialog] =
    useState(false);
  const [showSignatureSelectDialog, setShowSignatureSelectDialog] =
    useState(false);
  const [showSignatureConfirmDialog, setShowSignatureConfirmDialog] =
    useState(false);
  const [selectedSignatureType, setSelectedSignatureType] = useState<
    "SC" | "PO" | null
  >(null);
  const [showInviteCodeDialog, setShowInviteCodeDialog] = useState(false);

  const [confirmImporterSc] = useConfirmImporterScMutation();
  const [confirmImporterPo] = useConfirmImporterPoMutation();
  const [sendCompanyLinkCode, { isLoading: isSendLinkCodeLoading }] =
    useSendCompanyLinkCodeMutation();

  const { data: exportersData } = useGetExportersQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const { poHistories, isZeroPo } =
    contractDetailApi.endpoints.getImporterPoHistories.useQueryState(
      {
        contractId: Number(params.id),
      },
      {
        selectFromResult: ({ currentData, isError }) => {
          return {
            poHistories: currentData?.rows ?? [],
            isZeroPo: isError || !currentData?.rows?.length,
          };
        },
      },
    );

  const { signatureStatus, contractStatus, orderDateAt, contractData } =
    contractDetailApi.endpoints.getImporterContractDetail.useQueryState(
      {
        id: Number(params.id),
      },
      {
        selectFromResult: ({ currentData, isError, isFetching }) => {
          const isUnstable = isUndefined(currentData) || isError || isFetching;
          const isStable = !isUnstable;

          return {
            contractStatus: isStable
              ? currentData.contractStatus
              : "NOT_LINKED",
            signatureStatus: isStable
              ? currentData.signatureStatus || "DRAFT"
              : "DRAFT",
            orderDateAt:
              isStable && currentData.orderDateAt
                ? dayjs(currentData.orderDateAt).format("YYYY-MM-DD")
                : "",
            contractData: currentData
              ? {
                  ...currentData,
                  signatureStatus: currentData.signatureStatus || "DRAFT",
                }
              : undefined,
          };
        },
      },
    );

  const { signatureHistories, contractSignature } =
    contractDetailApi.endpoints.getImporterContractSignatureHistories.useQueryState(
      { id: Number(params.id) },
      {
        selectFromResult: ({ currentData }) => ({
          signatureHistories: currentData?.rows,
          contractSignature: currentData?.rows?.[0], // 가장 최신 서명 정보
        }),
      },
    );

  const isPoCreateButton =
    signatureStatus === "APPROVED" &&
    getContractCurrentVersion(poHistories).currentVersion >= 2 &&
    getContractCurrentVersion(poHistories).currentVersionHistoryLength === 1;

  // Get current step info for contract progress
  const { currentStepInfo } = useContractProgress({
    contractData,
    contractSignature,
    signatureHistories,
    userRole: "IMPORTER",
    contractId: Number(params.id),
  });

  // 거래처 직원 목록 가져오기
  const { data: buyerEmployeesData } = useGetBuyerEmployeesQuery(
    { id: contractData?.importerBuyerId || 0 },
    { skip: !contractData?.importerBuyerId },
  );

  const contactPersonList =
    buyerEmployeesData?.rows?.map((employee) => ({
      ...employee,
      uniqueId: employee.id.toString(),
    })) || [];

  // 초대코드 발송 핸들러
  const handleClientIntegrationInviteCode = async (
    data: GenerateBuyerEmployeeDto,
  ) => {
    try {
      await sendCompanyLinkCode({
        emailList: [data.email],
      }).unwrap();
      setShowInviteCodeDialog(false);
      alert.showAlert({
        type: "success",
        message: t("alert:sendSuccess"),
      });
    } catch (e: any) {
      const message = Array.isArray(e.data?.message)
        ? e.data.message[0]
        : e.data?.message || t("error:sendFailed");
      alert.showAlert({ type: "error", message });
    }
  };

  // 서명 요청 처리 함수
  const handleSignatureRequest = async () => {
    // 현재 스텝의 문서 타입을 사용하여 정확한 서명 타입 결정
    const currentStep = currentStepInfo.step;

    if (!currentStep || !currentStep.documentType) {
      alert.showAlert({ type: "error", message: t("error:unknown") });
      return;
    }

    // 현재 단계의 문서 타입에 따라 서명 타입 설정
    setSelectedSignatureType(currentStep.documentType);

    // 서명 확인 다이얼로그 표시
    setShowSignatureConfirmDialog(true);
  };

  // 서명 확인 후 처리
  const handleSignatureConfirm = () => {
    setShowSignatureConfirmDialog(false);

    // 서명 확인
    const signatures =
      exportersData?.signatureManagement?.simpleExporterSignatureMedias;
    const exporterUserType = (exportersData as any)?.exporterUserType;

    if (isNull(signatures)) {
      // 서명이 없는 경우
      if (exporterUserType !== "MANAGER") {
        setShowNoSignatureDialog(true);
      } else {
        setShowSignatureRequestDialog(true);
      }
      return;
    }

    // 서명이 2개인 경우
    const isTwoSign = signatures && signatures.length === 2;
    if (isTwoSign) {
      setShowSignatureSelectDialog(true);
      return;
    }

    // 서명이 1개인 경우 바로 요청
    const defaultSignatureMediaId =
      signatures?.find((sig: any) => sig.isDefault)?.id || signatures?.[0]?.id;
    if (defaultSignatureMediaId) {
      handleConfirmSignature(defaultSignatureMediaId).catch(() => {});
    }
  };

  // 서명 확인 처리
  const handleConfirmSignature = async (signatureMediaId: number) => {
    handleContentLoadingOn();

    try {
      if (selectedSignatureType === "PO") {
        await confirmImporterPo({
          id: Number(params.id),
          signatureMediaId,
        }).unwrap();

        alert.showAlert({
          type: "success",
          message: t("contract:status.notice.poSignaturePending.title"),
        });
      } else if (selectedSignatureType === "SC") {
        await confirmImporterSc({
          id: Number(params.id),
          signatureMediaId,
        }).unwrap();

        alert.showAlert({
          type: "success",
          message: t("contract:status.notice.scSignaturePending.title"),
        });
      }

      // 다이얼로그 닫기
      setShowSignatureSelectDialog(false);
      setShowNoSignatureDialog(false);
      setShowSignatureRequestDialog(false);
    } catch (e: any) {
      const message = Array.isArray(e.data?.message)
        ? e.data.message[0]
        : e.data?.message || t("error:unknown");
      alert.showAlert({ type: "error", message });
    } finally {
      handleContentLoadingOff();
    }
  };

  return (
    <>
      <TopContainer>
        <OrderDateText>
          {orderDateAt ? `Order Date ${orderDateAt}` : ""}
        </OrderDateText>
        <FlexEnd>
          {/* History Button */}
          <Button
            buttonSize={32}
            buttonGrade="secondary"
            onClick={historyDialogOn}
            style={{
              display: "flex",
              height: "32px",
              padding: "6px 12px 6px 8px",
              justifyContent: "center",
              alignItems: "center",
              gap: "4px",
              borderRadius: "8px",
              background: "#FFF",
              boxShadow:
                "0px 0px 0px 0.5px rgba(160, 164, 171, 0.20), 0px 1px 2px 0px rgba(5, 29, 57, 0.10)",
              border: "none",
              color: "#222729",
              fontFamily: "Pretendard",
              fontSize: "15px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "20px",
            }}
          >
            <Icon iconSrc={ClockIconSvg} iconSize={16} />
            {t("contract:history.button.viewHistory")}
          </Button>

          {/* View Contract Button - Hide when contract is complete or stage is final to avoid duplication */}
          {/* Also hide when no documents exist (both poNo and scNo are empty) */}
          {contractStatus !== "COMPLETE" &&
            currentStepInfo.step?.status !== "final" &&
            (poNo || scNo) && (
              <Button
                buttonSize={32}
                buttonGrade="secondary"
                onClick={() => {
                  setIsContractViewerOpen(true);
                }}
                style={{
                  display: "flex",
                  height: "32px",
                  padding: "6px 12px 6px 8px",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "4px",
                  borderRadius: "8px",
                  background: "#FFF",
                  boxShadow:
                    "0px 0px 0px 0.5px rgba(160, 164, 171, 0.20), 0px 1px 2px 0px rgba(5, 29, 57, 0.10)",
                  border: "none",
                  color: "#222729",
                  fontFamily: "Pretendard",
                  fontSize: "15px",
                  fontStyle: "normal",
                  fontWeight: 500,
                  lineHeight: "20px",
                }}
              >
                <Icon iconSrc={PreviewSvg} iconSize={16} />
                {t("contract:progress.button.viewContract")}
              </Button>
            )}

          {/* 버전에 따라 버튼 표시 결정 */}
          {(() => {
            const currentVersion = getContractCurrentVersion(
              poHistories || [],
            ).currentVersion;

            return (
              <StyledButton
                buttonSize={32}
                buttonGrade="secondary"
                onClick={() =>
                  navigate(
                    `${IMPORTER_PRIVATE_PATH.CONTRACT_EDIT_V2}/${params.id}`,
                  )
                }
                disabled={
                  isDisabledEdit ||
                  // 2차 계약 이상일 때는 항상 disabled
                  (currentVersion >= 2 && contractStatus === "COMPLETE") ||
                  currentStepInfo.step?.buttonAction === "CREATE_PO"
                }
              >
                <EditIcon
                  disabled={
                    isDisabledEdit ||
                    (currentVersion >= 2 && contractStatus === "COMPLETE") ||
                    currentStepInfo.step?.buttonAction === "CREATE_PO"
                  }
                />
                {t("contract:importer.detail.button.editButton")}
              </StyledButton>
            );
          })()}
        </FlexEnd>
      </TopContainer>

      {/* Contract Progress Bar */}
      <div style={{ marginBottom: 24 }}>
        <ContractProgressBar
          userRole="IMPORTER"
          contractData={contractData}
          contractSignature={contractSignature}
          signatureHistories={signatureHistories}
          contractId={Number(params.id)}
          onAction={(actionType) => {
            if (actionType === "VIEW_CONTRACT") {
              // 서명 완료 상태 계산
              const isPOFullySigned =
                contractSignature?.isExporterSignedPO &&
                contractSignature?.isImporterSignedPO;
              const isSCFullySigned =
                contractSignature?.isExporterSignedSC &&
                contractSignature?.isImporterSignedSC;

              // 플로우 타입 결정 (누가 먼저 시작했는지)
              // signatureHistories가 있으면 첫 번째 문서로 판단하고, 없으면 문서 존재 여부로 판단
              const isExporterLead = scNo && !poNo;
              const isImporterLead = poNo && !scNo;
              const flow = isExporterLead
                ? "EXPORTER_LEAD"
                : isImporterLead
                  ? "IMPORTER_LEAD"
                  : scNo
                    ? "EXPORTER_LEAD"
                    : "IMPORTER_LEAD";

              // 현재 단계 계산
              let currentStage = 1;
              if (isPOFullySigned && isSCFullySigned) {
                currentStage = 3;
              } else if (flow === "EXPORTER_LEAD" && isSCFullySigned) {
                currentStage = 2;
              } else if (flow === "IMPORTER_LEAD" && isPOFullySigned) {
                currentStage = 2;
              }

              // 3단계 - 모달로 보기
              if (currentStage === 3) {
                setIsContractViewerOpen(true);
              }
              // 1,2단계 - 풀스크린 뷰어로 보기
              else if (onOpenSignatureViewer) {
                // 어떤 문서를 봐야 하는지 결정하는 로직
                let documentToView: "SC" | "PO";
                
                // 수입자 입장에서 서명이 필요한 문서 확인
                const needsToSignSC = contractSignature?.isImporterSignedSC === false && 
                                     contractSignature?.isExporterSignedSC === true;
                const needsToSignPO = contractSignature?.isImporterSignedPO === false && 
                                     contractSignature?.isExporterSignedPO === true;
                
                if (needsToSignPO) {
                  // PO 서명이 필요하면 PO를 보여줌
                  documentToView = "PO";
                } else if (needsToSignSC) {
                  // SC 서명이 필요하면 SC를 보여줌
                  documentToView = "SC";
                } else {
                  // 그 외의 경우 현재 단계의 문서를 사용
                  if (flow === "EXPORTER_LEAD") {
                    // 수출자 주도: 1단계-SC, 2단계-PO
                    documentToView = currentStage === 1 ? "SC" : "PO";
                  } else {
                    // 수입자 주도: 1단계-PO, 2단계-SC
                    documentToView = currentStage === 1 ? "PO" : "SC";
                  }
                }

                onOpenSignatureViewer(documentToView);
              }
            } else if (actionType === "VIEW_REJECTION_REASON") {
              // 반려 사유 보기
              if (onContractNoticeAction) {
                onContractNoticeAction(actionType);
              }
            } else if (actionType === "LINK_PARTNER") {
              // 거래처 연동 초대코드 발송 모달 열기
              setShowInviteCodeDialog(true);
            } else if (actionType === "REQUEST_SIGNATURE") {
              // 서명 요청 - 직접 서명 처리
              handleSignatureRequest();
            } else if (onContractNoticeAction) {
              onContractNoticeAction();
            }
          }}
          rejectReason={rejectReason}
          contractId={Number(params.id)}
        />
      </div>

      {/* Shipment Status Section - Only show when contract is linked (both SC and PO fully signed) */}
      {contractSignature?.isExporterSignedSC &&
        contractSignature?.isImporterSignedSC &&
        contractSignature?.isExporterSignedPO &&
        contractSignature?.isImporterSignedPO && (
          <SectionCard>
            <ShipmentStatusContainer>
              {/* Status */}
              <ShipmentStatusTextGrid>
                <ShipmentStatusText>
                  <Typo typoType="b9m" color="gray6">
                    {t(
                      "contract:importer.detail.shipmentList.balanceOfContract",
                    )}
                  </Typo>
                  <Typo typoType="h4" color="blue2">
                    {balanceOfContractV2}
                  </Typo>
                </ShipmentStatusText>

                <ShipmentStatusText>
                  <Typo typoType="b9m" color="gray6">
                    {t(
                      "contract:importer.detail.shipmentList.totalShippingWeight",
                    )}
                  </Typo>
                  <Typo typoType="h4" color="blue2">
                    {totalShippingWeightV2}
                  </Typo>
                </ShipmentStatusText>
              </ShipmentStatusTextGrid>

              {/* Status Percentage*/}
              <StatusPercentageContainer>
                {/* Percentage Tooltip */}
                <StyledTooltip
                  tooltipColor="green2"
                  placement="leftTop"
                  $isInnerWindow
                  left={
                    (percentOfShipment ?? 0) >= 100
                      ? 100
                      : (percentOfShipment ?? 0)
                  }
                  triggerComponent={
                    <PercentageContainer>
                      <PercentageColor
                        percent={
                          (percentOfShipment ?? 0) >= 100
                            ? 100
                            : (percentOfShipment ?? 0)
                        }
                        $isInnerWindow
                      />
                      <PercentageOverlay />
                    </PercentageContainer>
                  }
                >
                  <Typo color="white" typoType="b10m">
                    {percentOfShipment}%
                  </Typo>
                </StyledTooltip>

                {/* 0 ~ 100% */}
                <TopPercentage>
                  <Typo typoType="b10m" color="gray7">
                    0%
                  </Typo>
                  <Typo typoType="b10m" color="gray7">
                    100%
                  </Typo>
                </TopPercentage>

                {/* 선적 비율 */}
                <PercentageTypo>
                  <Typo typoType="b9m" color="gray6">
                    {t("contract:importer.detail.shipmentList.shipmentRate")}
                  </Typo>
                  <Typo typoType="h4" color="green1">
                    {(typeof percentOfShipment === "number"
                      ? percentOfShipment
                      : 0
                    )
                      .toFixed(2)
                      .padStart(5, "0")}
                    %
                  </Typo>
                </PercentageTypo>
              </StatusPercentageContainer>
            </ShipmentStatusContainer>
          </SectionCard>
        )}

      {/* Contents */}
      <BuyerCard />

      <ContractInformationCard isEditAuth={isEditAuth} />

      {/* Contract Viewer Modal */}
      <Suspense fallback={<Loader />}>
        <ContractViewerModal
          contractId={Number(params.id)}
          userType="IMPORTER"
          isOpen={isContractViewerOpen}
          onClose={() => setIsContractViewerOpen(false)}
          poNo={poNo}
          scNo={scNo}
          contractStatus={contractStatus}
          useGetPoHistoriesQuery={useLazyGetImporterPoHistoriesQuery}
          useGetScHistoriesQuery={useLazyGetImporterScHistoriesQuery}
        />
      </Suspense>

      {/* 서명 확인 다이얼로그 */}
      <AlertDialog
        open={showSignatureConfirmDialog}
        title={t("contract:importer.detail.alertDialog.signContractTitle")}
        description={t(
          "contract:importer.detail.alertDialog.signContractDescription",
        )}
        okText={t("common:ok")}
        cancelText={t("common:cancel")}
        onOk={handleSignatureConfirm}
        onCancel={() => setShowSignatureConfirmDialog(false)}
        onOpenChange={(open) => setShowSignatureConfirmDialog(open)}
      />

      {/* 서명 관련 다이얼로그들 */}
      <NoRegisteredSignatureAlertDialog
        open={showNoSignatureDialog}
        onOpenChange={() => setShowNoSignatureDialog(false)}
        onOk={() => {
          navigate(
            `${IMPORTER_PRIVATE_PATH.COMPANY_INFORMATION}/signature`,
          );
        }}
        onCancel={() => setShowNoSignatureDialog(false)}
      />

      <SignatureRequestAlertDialog
        open={showSignatureRequestDialog}
        onOpenChange={() => setShowSignatureRequestDialog(false)}
        onCancel={() => setShowSignatureRequestDialog(false)}
      />

      <SignatureSelectDialog
        open={showSignatureSelectDialog}
        onOpenChange={() => setShowSignatureSelectDialog(false)}
        onSave={handleConfirmSignature}
      />

      {/* 거래처 연동 초대코드 발송 모달 */}
      {showInviteCodeDialog && (
        <ClientIntegrationInviteCodeDialog
          onClose={() => setShowInviteCodeDialog(false)}
          onSave={handleClientIntegrationInviteCode}
          personList={contactPersonList}
          isLoading={isSendLinkCodeLoading}
        />
      )}
    </>
  );
};

export default ContractInformationTab;

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const EditIcon = styled(EditSvg)<{ disabled?: boolean }>`
  width: 16px;
  height: 16px;

  path {
    fill: ${({ disabled }) => (disabled ? colorSet.gray8 : colorSet.indigo)};
  }
`;

const StyledSectionCardWithoutHeader = styled(SectionCardWithoutHeader)`
  background: ${colorSet.white};
`;

const FlexEnd = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const TopContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const OrderDateText = styled.span`
  color: var(--Gray-Gray-6, #6e777c);
  font-family: Pretendard;
  font-size: 19px;
  font-style: normal;
  font-weight: 500;
  line-height: 26px;
`;

const ShipmentStatusContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
`;

const ShipmentStatusTextGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 176px);
  align-items: start;
`;

const ShipmentStatusText = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  gap: 8px;
  padding: 16px 8px;

  &:first-child {
    border-right: 1px solid ${colorSet.gray10};
  }
`;

const StatusPercentageContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  padding-right: 20px;
`;

const TopPercentage = styled.div`
  display: flex;
  justify-content: space-between;
`;

const PercentageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 16px;
  margin: 24px 0 8px 0;
`;

const PercentageOverlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 20px;
  background: ${colorSet.gray9};
`;

const percentageAnimation = (number: number) => keyframes`
  0% {
    width: 0;
  }
  100% {
    width: ${Math.round(number)}%
  }
`;

const percentageTooltipAnimation = (number: number) => keyframes`
  0% {
    left: 0;
  }
  100% {
    left: ${Math.round(number)}%;
  }
`;

const PercentageColor = styled.div<{
  percent: number;
  $isInnerWindow: boolean;
}>`
  position: absolute;
  height: 100%;
  border-radius: 20px;
  background: ${colorSet.green2};
  z-index: 1;
  animation: ${({ percent, $isInnerWindow }) =>
    $isInnerWindow ? percentageAnimation(percent) : null};
  animation-duration: 500ms;
  animation-fill-mode: both;
`;

const PercentageTypo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledTooltip = styled(Tooltip)<{
  left: number;
  $isInnerWindow: boolean;
}>`
  bottom: 30px;
  animation: ${({ left, $isInnerWindow }) =>
    $isInnerWindow ? percentageTooltipAnimation(left) : null};
  animation-duration: 500ms;
  animation-fill-mode: both;
`;

const fadeInAnimation = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SectionCard = styled(StyledSectionCardWithoutHeader)`
  padding: 35px 16px;
  animation: ${fadeInAnimation} 0.3s ease-in-out;
`;
